import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/auth';
import { useCampaignCreation } from '@/hooks/useCampaignCreation';
import { useSmsStatus } from '@/context/SmsStatusContext';
import CampaignSteps from './create/CampaignSteps';
import CampaignContent from './create/CampaignContent';
import CampaignNavigation from './create/CampaignNavigation';
import { toast } from '@/hooks/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

interface CampaignFormProps {
  campaignId?: string | null;
  onSuccess: () => void;
  onCancel: () => void;
}

/**
 * CampaignForm - Reusable campaign creation/editing form component
 *
 * This component extracts the core campaign creation logic from CampaignCreate page
 * to enable inline rendering within tabs or other contexts. It manages the multi-step
 * wizard flow and handles campaign data via the useCampaignCreation hook.
 *
 * @param campaignId - Optional campaign ID for editing (future feature)
 * @param onSuccess - Callback fired when campaign is successfully saved
 * @param onCancel - Callback fired when user cancels the form
 */
const CampaignForm: React.FC<CampaignFormProps> = ({
  campaignId,
  onSuccess,
  onCancel
}) => {
  const { user } = useAuth();
  const [activeStep, setActiveStep] = useState('details');
  const { organisationSmsStatus, checkOrganisationSmsStatus } = useSmsStatus();
  const [smsEnabled, setSmsEnabled] = useState<boolean>(false);

  // Check SMS status on mount
  useEffect(() => {
    const checkStatus = async () => {
      if (user?.organisationId) {
        const enabled = await checkOrganisationSmsStatus(user.organisationId);
        setSmsEnabled(enabled);
      }
    };
    checkStatus();
  }, [user?.organisationId, checkOrganisationSmsStatus]);

  const {
    campaignData,
    updateCampaignData,
    audienceSize,
    audienceLoading,
    audience,
    isStepComplete,
    isStepNavigable,
    saveCampaign,
    isSubmitting,
    handleStepChange
  } = useCampaignCreation(user?.restaurants);

  // Call handleStepChange when activeStep changes
  useEffect(() => {
    handleStepChange(activeStep);
  }, [activeStep, handleStepChange]);

  // User not authenticated - should not happen if parent checks properly
  if (!user) {
    return (
      <div className="py-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            You must be logged in to create a campaign. Please refresh the page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // SMS not enabled for organization
  if (!smsEnabled) {
    return (
      <div className="py-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            SMS features need to be enabled for your organisation to create campaigns.
            Please contact your administrator or enable SMS in your organisation settings.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  /**
   * Handle navigation to next step in the wizard
   */
  const handleNextStep = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }

    switch (activeStep) {
      case 'details':
        setActiveStep('audience');
        break;
      case 'audience':
        setActiveStep('message');
        break;
      case 'message':
        setActiveStep('review');
        break;
      default:
        break;
    }
  };

  /**
   * Handle navigation to previous step in the wizard
   */
  const handlePreviousStep = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }

    switch (activeStep) {
      case 'audience':
        setActiveStep('details');
        break;
      case 'message':
        setActiveStep('audience');
        break;
      case 'review':
        setActiveStep('message');
        break;
      default:
        break;
    }
  };

  /**
   * Handle campaign save - validates, saves, and calls onSuccess callback
   */
  const handleSaveCampaign = async (): Promise<boolean> => {
    console.log("CampaignForm: handleSaveCampaign called with user:", user?.id);

    try {
      if (!user) {
        console.error("No user found when trying to save campaign");
        toast({
          title: "Authentication required",
          description: "You must be logged in to save a campaign.",
          variant: "destructive"
        });
        return false;
      }

      const success = await saveCampaign(user);
      console.log("CampaignForm: Save campaign result:", success);

      if (success) {
        toast({
          title: "Campaign saved successfully",
          description: "Your campaign has been saved and is ready to go.",
          variant: "default"
        });

        // Call parent success handler instead of navigation
        onSuccess();
        return true;
      } else {
        console.error("Failed to save campaign - returned false");
        toast({
          title: "Failed to save campaign",
          description: "There was a problem saving your campaign. Please try again.",
          variant: "destructive"
        });
        return false;
      }
    } catch (error) {
      console.error('Error saving campaign:', error);
      toast({
        title: "Failed to save campaign",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive"
      });
      return false;
    }
  };

  const steps = [
    { id: 'details', label: 'Campaign Details' },
    { id: 'audience', label: 'Audience Selection' },
    { id: 'message', label: 'Compose Message' },
    { id: 'review', label: 'Review & Save' },
  ];

  return (
    <div className="space-y-8">
      <CampaignSteps
        steps={steps}
        activeStep={activeStep}
        isStepComplete={isStepComplete}
        isStepNavigable={isStepNavigable}
        setActiveStep={setActiveStep}
      />

      <div className="space-y-8 animate-slide-in">
        <CampaignContent
          activeStep={activeStep}
          campaignData={campaignData}
          updateCampaignData={updateCampaignData}
          audienceSize={audienceSize}
          audienceLoading={audienceLoading}
          audience={audience}
          isSubmitting={isSubmitting}
          userRestaurants={user.restaurants}
          onSaveCampaign={handleSaveCampaign}
        />

        <CampaignNavigation
          activeStep={activeStep}
          isStepComplete={isStepComplete}
          handlePreviousStep={handlePreviousStep}
          handleNextStep={handleNextStep}
          handleSaveCampaign={handleSaveCampaign}
          isSubmitting={isSubmitting}
        />
      </div>
    </div>
  );
};

export default CampaignForm;
